import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import { scrollToView, showToaster } from "components/Common/common";
import { useSelector } from "react-redux";
import axiosInstance from "components/Common/ApiManagement/axiosInstance";
import { ApiNames, METABASE, VIEW_TYPE_OPTIONS } from "components/Common/ApiManagement/apiServices";
import Dropzone from "react-dropzone"
import * as Yup from "yup"
import "./style.css"
import {
  FileUp,
  Upload,
  FileSpreadsheet,
  Settings,
  CheckCircle,
  AlertCircle,
  X,
  Database,
  Eye
} from 'lucide-react';

const ReferranceTableCreate = props => {
  document.title = "Reference Table Management"
  const user = useSelector((state) => state?.users?.users);
  const navigate = useNavigate();

  const [selectedFiles, setselectedFiles] = useState([])
  const [uploadingLoad, setUploadLoad] = useState(false)
  const [viewUploadingLoad, setViewUploadLoad] = useState(true)
  const [selectedView, setSelectedView] = useState(0);

  const valSchema = Yup.object().shape({
    Name: Yup.string().required("Name is required"),
    ["Url Address"]: Yup.string().required("Url Address is required"),
    ["Parent Website"]: Yup.string().required("Parent Website is required"),
    Environment: Yup.string()
      .oneOf(["dev", "qa", "uat", "prod"], "Environment must be either prod, dev, qa or uat")
      .required("Environment is required"),
  });

  const urlSchema = Yup.array().of(valSchema);

  useEffect(() => {
    props.setBreadcrumbItems("Reference Table Management", [])
  }, [])

  useEffect(() => {
    const fetchViewType = async () => {
      try {
        const res = await axiosInstance.post(ApiNames.VIEW_METHOD_GET);
        setSelectedView(res.data.data);
      } catch (err) {
        console.error('Error fetching view config', err);
      } finally {
        setViewUploadLoad(false)
      }
    };
    fetchViewType();
  }, []);

  const handleDoc = async (file) => {
    if (file) {
      const reader = new FileReader()
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        const json = XLSX.utils.sheet_to_json(worksheet)
        try {
          await urlSchema.validate(json, { abortEarly: false })
        } catch (err) {
          console.log(err.errors);
          showToaster(0, err.errors[0])
          return
        }
      }
      reader.readAsArrayBuffer(file)
    }
  }

  const handleAcceptedFiles = files => {
    handleDoc(files[0])
    files.map(file =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      }),
    )
    setselectedFiles(files)
  }

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
  }

  const handleViewUpload = async (e) => {
    const newValue = parseInt(e.target.value);
    setSelectedView(newValue);
    setViewUploadLoad(true)

    try {
      const response = await axiosInstance.post(ApiNames.VIEW_METHOD_UPLOAD, { viewType: newValue });
      if (response.data.status) {
        showToaster(1, "View type saved successfully")
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showToaster(0, "Failed to save view type")
    } finally {
      setViewUploadLoad(false)
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) {
      showToaster(0, 'Please select a file!');
      return;
    }
    setUploadLoad(true)
    const formData = new FormData();
    formData.append('file', files[0]);

    try {
      const response = await axiosInstance.post(ApiNames.REFERRENCE_TABLE_CREATE, formData);
      if (response.data.status) {
        showToaster(1, response.data.message)
        setselectedFiles([])
      } else {
        showToaster(0, "Upload failed")
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showToaster(0, "Error uploading file")
    } finally {
      setUploadLoad(false)
    }
  };

  const removeFile = () => {
    setselectedFiles([])
  }

  return (
    <div className="reference-table-page">
      {/* Header */}
      <div className="ref-page-header">
        <div className="ref-header-content">
          <div className="ref-header-title-row">
            <Database className="ref-header-icon" />
            <h1>Reference Table Management</h1>
          </div>
          <p className="ref-header-subtitle">
            Upload and manage reference data tables for compliance framework mapping
          </p>
        </div>
        <button
          className="ref-metabase-btn"
          onClick={() => navigate("/comprehensiveCompliance")}
        >
          <Settings className="btn-icon" />
          Metabase Edit
        </button>
      </div>

      {/* Main Content */}
      <div className="ref-content-wrapper">
        <div className="ref-main-card">
          {/* View Type Selector */}
          <div className="ref-view-selector-section">
            <div className="view-selector-header">
              <Eye className="section-icon" />
              <h3>View Type Configuration</h3>
            </div>
            {viewUploadingLoad ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <span>Loading view types...</span>
              </div>
            ) : (
              <div className="view-selector-wrapper">
                <label className="view-label">Select View Type:</label>
                <select
                  className="view-select"
                  value={selectedView}
                  onChange={handleViewUpload}
                  disabled={viewUploadingLoad}
                >
                  {VIEW_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* File Upload Section */}
          <div className="ref-upload-section">
            <div className="upload-header">
              <FileSpreadsheet className="section-icon" />
              <h3>Upload Excel File</h3>
            </div>

            <Dropzone
              onDrop={acceptedFiles => handleAcceptedFiles(acceptedFiles)}
              accept={{
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                'application/vnd.ms-excel': ['.xls']
              }}
            >
              {({ getRootProps, getInputProps, isDragActive }) => (
                <div
                  className={`ref-dropzone ${isDragActive ? 'dropzone-active' : ''} ${selectedFiles.length > 0 ? 'dropzone-has-file' : ''}`}
                  {...getRootProps()}
                >
                  <input {...getInputProps()} />
                  <div className="dropzone-content">
                    <div className="dropzone-icon-wrapper">
                      <FileUp className="dropzone-icon" />
                    </div>
                    <h4 className="dropzone-title">
                      {isDragActive ? "Drop your file here" : "Drop Excel file here or click to browse"}
                    </h4>
                    <p className="dropzone-subtitle">
                      Supported formats: .xlsx, .xls
                    </p>
                  </div>
                </div>
              )}
            </Dropzone>

            {/* File Preview */}
            {selectedFiles.length > 0 && (
              <div className="file-preview-section">
                {selectedFiles.map((file, i) => (
                  <div className="file-preview-card" key={i}>
                    <div className="file-preview-left">
                      <div className="file-icon-wrapper">
                        <FileSpreadsheet className="file-icon" />
                      </div>
                      <div className="file-info">
                        <div className="file-name">{file.name}</div>
                        <div className="file-size">{file.formattedSize}</div>
                      </div>
                    </div>
                    <button
                      className="file-remove-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile();
                      }}
                    >
                      <X className="remove-icon" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upload Button */}
          <div className="ref-actions-section">
            <button
              className={`ref-upload-btn ${uploadingLoad ? 'btn-loading' : ''} ${selectedFiles.length === 0 ? 'btn-disabled' : ''}`}
              onClick={() => handleUpload(selectedFiles)}
              disabled={uploadingLoad || selectedFiles.length === 0}
            >
              {uploadingLoad ? (
                <>
                  <div className="btn-spinner"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="btn-icon" />
                  Upload File
                </>
              )}
            </button>
          </div>

          {/* Info Box */}
          <div className="ref-info-box">
            <div className="info-icon-wrapper">
              <AlertCircle className="info-icon" />
            </div>
            <div className="info-content">
              <h4>Upload Requirements</h4>
              <ul>
                <li>Excel file must contain: Name, Url Address, Parent Website, Environment</li>
                <li>Environment must be one of: prod, dev, qa, or uat</li>
                <li>All fields are required</li>
                <li>Maximum file size: 10MB</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default connect(null, { setBreadcrumbItems })(ReferranceTableCreate)
